import {
  ChangeEvent,
  DragEvent,
  KeyboardEvent,
  MouseEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import ButtonBase from '@material-ui/core/ButtonBase';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import LinkIcon from '@material-ui/icons/Link';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import PublishIcon from '@material-ui/icons/Publish';
import UnfoldLessIcon from '@material-ui/icons/UnfoldLess';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import { useKnowledgeBuilder } from './KnowledgeBuilderProvider';
import type { KnowledgeDocument } from '../../api';
import {
  KnowledgeBuilderColors,
  useKnowledgeBuilderColors,
} from '../../theme/knowledgeBuilderColors';

type TreeNode =
  | {
      kind: 'folder';
      name: string;
      path: string;
      children: TreeNode[];
    }
  | {
      kind: 'file';
      name: string;
      path: string;
      document: KnowledgeDocument;
    };

type DistributiveOmit<T, K extends PropertyKey> = T extends any
  ? Omit<T, K>
  : never;

type ContextMenuState =
  | {
      x: number;
      y: number;
      kind: 'root';
      path: '';
    }
  | {
      x: number;
      y: number;
      kind: 'folder';
      path: string;
    }
  | {
      x: number;
      y: number;
      kind: 'file';
      path: string;
      document: KnowledgeDocument;
    };

type ClipboardState =
  | {
      kind: 'file';
      document: KnowledgeDocument;
    }
  | {
      kind: 'folder';
      path: string;
      documents: KnowledgeDocument[];
    };

type FocusedNode =
  | {
      kind: 'root';
      path: '';
    }
  | {
      kind: 'folder';
      path: string;
    }
  | {
      kind: 'file';
      path: string;
    };

export const DocumentTree = ({
  selectedPath,
  searchQuery = '',
}: {
  selectedPath?: string;
  searchQuery?: string;
}) => {
  const {
    addUrl,
    createDocument,
    deleteDocument,
    deleteFolder,
    directories,
    documents,
    renameDocument,
    renameFolder,
    selectDocument,
    uploadFiles,
    uiOptions,
  } = useKnowledgeBuilder();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [url, setUrl] = useState('');
  const [dragOverFolder, setDragOverFolder] = useState<string>();
  const [contextMenu, setContextMenu] = useState<ContextMenuState>();
  const [clipboard, setClipboard] = useState<ClipboardState>();
  const [focusedNode, setFocusedNode] = useState<FocusedNode>();
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    () => new Set(),
  );
  const colors = useKnowledgeBuilderColors();
  const styles = useMemo(() => buildStyles(colors), [colors]);
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const hiddenDocumentPaths = uiOptions.hiddenDocumentPaths;
  const visibleDocuments = useMemo(
    () =>
      normalizedSearch
        ? documents.filter(
            document =>
              !isHiddenDocumentPath(document.path, hiddenDocumentPaths) &&
              document.path.toLowerCase().includes(normalizedSearch),
          )
        : documents.filter(
            document =>
              !isHiddenDocumentPath(document.path, hiddenDocumentPaths),
          ),
    [documents, hiddenDocumentPaths, normalizedSearch],
  );
  const visibleDirectories = useMemo(
    () =>
      directories.filter(
        directory => !isHiddenDocumentPath(directory, hiddenDocumentPaths),
      ),
    [directories, hiddenDocumentPaths],
  );
  const tree = useMemo(
    () =>
      buildTree(visibleDocuments, normalizedSearch ? [] : visibleDirectories),
    [normalizedSearch, visibleDirectories, visibleDocuments],
  );
  const treeExpanded = expandedFolders.size > 0;

  const createFile = (folderPath = '') => {
    const defaultPath = nextAvailableFilePath(
      joinPath(folderPath, 'new-page.md'),
      documents,
    );
    const name = window.prompt('File path', defaultPath);
    if (name) {
      createDocument(normalizePath(name));
    }
  };

  const createFolder = (folderPath = '') => {
    const defaultPath = nextAvailableFolderPath(
      joinPath(folderPath, 'new-folder'),
      documents,
    );
    const name = window.prompt('Folder path', defaultPath);
    if (name) {
      createDocument(joinPath(normalizePath(name), 'index.md'));
    }
  };

  const toggleFolder = (path: string) => {
    setExpandedFolders(current => {
      const next = new Set(current);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const expandAllFolders = () => {
    setExpandedFolders(new Set(directories));
  };

  const collapseAllFolders = () => {
    setExpandedFolders(new Set());
  };

  const toggleAllFolders = () => {
    if (treeExpanded) {
      collapseAllFolders();
    } else {
      expandAllFolders();
    }
  };

  const renameFile = (document: KnowledgeDocument) => {
    const nextPath = window.prompt('Rename file', document.path);
    if (nextPath && nextPath !== document.path) {
      renameDocument(document.path, normalizePath(nextPath));
    }
  };

  const renameDirectory = (path: string) => {
    const nextPath = window.prompt('Rename folder', path);
    if (nextPath && nextPath !== path) {
      renameFolder(path, normalizePath(nextPath));
    }
  };

  const removeFile = (document: KnowledgeDocument) => {
    if (window.confirm(`Delete ${document.path}?`)) {
      deleteDocument(document.path);
    }
  };

  const removeFolder = (path: string) => {
    if (window.confirm(`Delete ${path} and all files inside it?`)) {
      deleteFolder(path);
    }
  };

  const copyFile = (document: KnowledgeDocument) => {
    setClipboard({ kind: 'file', document });
  };

  const copyFolder = (path: string) => {
    const prefix = `${path.replace(/\/$/, '')}/`;
    setClipboard({
      kind: 'folder',
      path,
      documents: documents.filter(document => document.path.startsWith(prefix)),
    });
  };

  const pasteClipboard = async (targetFolder = '') => {
    if (!clipboard) {
      return;
    }
    if (clipboard.kind === 'file') {
      const targetPath = nextAvailableFilePath(
        joinPath(
          targetFolder,
          clipboard.document.path.split('/').pop() ?? 'copy.md',
        ),
        documents,
      );
      await createDocument(targetPath, clipboard.document.content);
      return;
    }

    const sourcePrefix = `${clipboard.path.replace(/\/$/, '')}/`;
    const folderName = clipboard.path.split('/').pop() ?? 'folder';
    const targetPrefix = joinPath(targetFolder, folderName);
    for (const document of clipboard.documents) {
      const relativePath = document.path.startsWith(sourcePrefix)
        ? document.path.slice(sourcePrefix.length)
        : document.path.split('/').pop() ?? document.path;
      const targetPath = nextAvailableFilePath(
        joinPath(targetPrefix, relativePath),
        documents,
      );
      await createDocument(targetPath, document.content);
    }
  };

  const copySelectedPath = () => {
    if (!selectedPath) {
      return;
    }
    const document = documents.find(item => item.path === selectedPath);
    if (document) {
      copyFile(document);
    }
  };

  const pasteToSelectedFolder = () => {
    const selectedDocument =
      focusedNode?.kind === 'file'
        ? documents.find(document => document.path === focusedNode.path)
        : selectedPath
        ? documents.find(document => document.path === selectedPath)
        : undefined;
    if (focusedNode?.kind === 'folder') {
      pasteClipboard(focusedNode.path);
      return;
    }
    const targetFolder = selectedDocument
      ? selectedDocument.path.split('/').slice(0, -1).join('/')
      : '';
    pasteClipboard(targetFolder);
  };

  const deleteFocusedNode = () => {
    if (focusedNode?.kind === 'folder') {
      removeFolder(focusedNode.path);
      return;
    }
    const targetPath =
      focusedNode?.kind === 'file' ? focusedNode.path : selectedPath;
    const document = targetPath
      ? documents.find(item => item.path === targetPath)
      : undefined;
    if (document) {
      removeFile(document);
    }
  };

  const onFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    await uploadFiles(Array.from(event.target.files ?? []));
    event.target.value = '';
  };

  const submitUrl = () => {
    if (url.trim()) {
      addUrl(url.trim());
      setUrl('');
    }
  };

  const onUrlKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      submitUrl();
    }
  };

  const showContextMenu = (
    event: MouseEvent<HTMLElement>,
    nextContextMenu: DistributiveOmit<ContextMenuState, 'x' | 'y'>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      ...nextContextMenu,
      x: event.clientX,
      y: event.clientY,
    } as ContextMenuState);
  };

  const onFolderDrop = (
    event: DragEvent<HTMLElement>,
    targetFolder: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOverFolder(undefined);
    const sourcePath = event.dataTransfer.getData('text/x-knowledge-path');
    const sourceKind = event.dataTransfer.getData('text/x-knowledge-kind');
    if (!sourcePath || sourcePath === targetFolder) {
      return;
    }
    if (sourceKind === 'folder') {
      const folderName = sourcePath.split('/').pop() ?? sourcePath;
      const nextPath = joinPath(targetFolder, folderName);
      if (
        nextPath !== sourcePath &&
        !targetFolder.startsWith(`${sourcePath}/`)
      ) {
        renameFolder(sourcePath, nextPath);
      }
      return;
    }
    if (sourceKind === 'file') {
      const fileName = sourcePath.split('/').pop() ?? sourcePath;
      const nextPath = joinPath(targetFolder, fileName);
      if (nextPath !== sourcePath) {
        renameDocument(sourcePath, nextPath);
      }
    }
  };

  return (
    <div
      style={styles.treeShell}
      tabIndex={0}
      onClick={() => setContextMenu(undefined)}
      onKeyDown={event => {
        const target = event.target;
        if (
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement
        ) {
          return;
        }
        if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault();
          deleteFocusedNode();
          return;
        }
        if (!(event.metaKey || event.ctrlKey)) {
          return;
        }
        if (event.key.toLowerCase() === 'c') {
          event.preventDefault();
          copySelectedPath();
        }
        if (event.key.toLowerCase() === 'v') {
          event.preventDefault();
          pasteToSelectedFolder();
        }
      }}
    >
      <div style={styles.treeHeader}>
        <Typography variant="caption" style={styles.treeHeaderText} noWrap>
          Explorer
        </Typography>
      </div>
      {uiOptions.allowLinks && (
        <div style={styles.sourceBar}>
          <input
            value={url}
            onChange={event => setUrl(event.target.value)}
            placeholder="Add URL"
            onKeyDown={onUrlKeyDown}
            style={styles.sourceBarInput}
          />
          <Tooltip title="Add URL">
            <button
              type="button"
              onClick={submitUrl}
              style={styles.smallIconButton}
            >
              <LinkIcon style={{ fontSize: 15 }} />
            </button>
          </Tooltip>
        </div>
      )}
      <div
        onContextMenu={event =>
          showContextMenu(event, { kind: 'root', path: '' })
        }
        onClick={() => setFocusedNode({ kind: 'root', path: '' })}
        style={styles.rootHeader}
      >
        <div style={styles.rootHeaderRow}>
          <Typography variant="caption" style={styles.rootText} noWrap>
            Root
          </Typography>
        </div>
        <div style={styles.rootActions}>
          <Tooltip title="New file">
            <IconButton
              size="small"
              onClick={event => {
                event.stopPropagation();
                createFile();
              }}
              style={styles.rootActionButton}
            >
              <NoteAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="New folder">
            <IconButton
              size="small"
              onClick={event => {
                event.stopPropagation();
                createFolder();
              }}
              style={styles.rootActionButton}
            >
              <CreateNewFolderIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Upload files">
            <IconButton
              size="small"
              onClick={event => {
                event.stopPropagation();
                fileInputRef.current?.click();
              }}
              style={styles.rootActionButton}
            >
              <PublishIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={treeExpanded ? 'Collapse tree' : 'Expand tree'}>
            <IconButton
              size="small"
              onClick={event => {
                event.stopPropagation();
                toggleAllFolders();
              }}
              style={styles.rootActionButton}
            >
              {treeExpanded ? (
                <UnfoldLessIcon fontSize="small" />
              ) : (
                <UnfoldMoreIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </div>
      </div>
      <input
        ref={fileInputRef}
        hidden
        multiple
        type="file"
        accept=".md,.txt,.yml,.yaml,.json,.css,.svg,.png,.pdf,.doc,.docx,.xls,.xlsx,text/*,application/json,image/png,image/svg+xml,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={onFiles}
      />
      <div
        onContextMenu={event =>
          showContextMenu(event, { kind: 'root', path: '' })
        }
        onDragOver={event => {
          event.preventDefault();
          setDragOverFolder('');
        }}
        onDragLeave={event => {
          if (isLeavingElement(event)) {
            setDragOverFolder(undefined);
          }
        }}
        onDrop={event => onFolderDrop(event, '')}
        style={styles.treeBody}
      >
        {visibleDocuments.length === 0 && (
          <Typography style={styles.emptyTree}>
            {normalizedSearch ? 'No matching files.' : 'No documents loaded.'}
          </Typography>
        )}
        {tree.map(node => (
          <TreeNodeView
            key={node.path}
            colors={colors}
            activeDocumentPath={selectedPath}
            expandedFolders={expandedFolders}
            dragOverFolder={dragOverFolder}
            forceExpanded={Boolean(normalizedSearch)}
            node={node}
            depth={0}
            onDeleteFile={removeFile}
            onDeleteFolder={removeFolder}
            onDragOverFolder={setDragOverFolder}
            onDropOnFolder={onFolderDrop}
            onRenameFile={renameFile}
            onRenameFolder={renameDirectory}
            onSelectDocument={selectDocument}
            onSetFocusedNode={setFocusedNode}
            onShowContextMenu={showContextMenu}
            onToggleFolder={toggleFolder}
          />
        ))}
      </div>
      {contextMenu && (
        <ContextMenu
          colors={colors}
          contextMenu={contextMenu}
          onClose={() => setContextMenu(undefined)}
          onCreateFile={createFile}
          onCreateFolder={createFolder}
          onCopyFile={copyFile}
          onCopyFolder={copyFolder}
          onRenameFile={renameFile}
          onRenameFolder={renameDirectory}
          onPaste={pasteClipboard}
          onRemoveFile={removeFile}
          onRemoveFolder={removeFolder}
          onSelectDocument={selectDocument}
          onUpload={() => fileInputRef.current?.click()}
        />
      )}
    </div>
  );
};

const TreeNodeView = ({
  colors,
  activeDocumentPath,
  expandedFolders,
  dragOverFolder,
  depth,
  forceExpanded,
  node,
  onDeleteFile,
  onDeleteFolder,
  onDragOverFolder,
  onDropOnFolder,
  onRenameFile,
  onRenameFolder,
  onSelectDocument,
  onSetFocusedNode,
  onShowContextMenu,
  onToggleFolder,
}: {
  colors: KnowledgeBuilderColors;
  activeDocumentPath?: string;
  expandedFolders: Set<string>;
  dragOverFolder?: string;
  depth: number;
  forceExpanded: boolean;
  node: TreeNode;
  onDeleteFile(document: KnowledgeDocument): void;
  onDeleteFolder(path: string): void;
  onDragOverFolder(path: string | undefined): void;
  onDropOnFolder(event: DragEvent<HTMLElement>, path: string): void;
  onRenameFile(document: KnowledgeDocument): void;
  onRenameFolder(path: string): void;
  onSelectDocument(path: string): void;
  onSetFocusedNode(node: FocusedNode): void;
  onShowContextMenu(
    event: MouseEvent<HTMLElement>,
    nextContextMenu: DistributiveOmit<ContextMenuState, 'x' | 'y'>,
  ): void;
  onToggleFolder(path: string): void;
}) => {
  if (node.kind === 'folder') {
    const expanded = forceExpanded || expandedFolders.has(node.path);
    const isDropTarget = dragOverFolder === node.path;
    return (
      <div style={{ position: 'relative' }}>
        <div
          draggable
          onClick={() => onSetFocusedNode({ kind: 'folder', path: node.path })}
          onContextMenu={event =>
            onShowContextMenu(event, { kind: 'folder', path: node.path })
          }
          onDragStart={event => {
            event.stopPropagation();
            event.dataTransfer.setData('text/x-knowledge-kind', 'folder');
            event.dataTransfer.setData('text/x-knowledge-path', node.path);
          }}
          onDragEnd={() => onDragOverFolder(undefined)}
          onDragEnter={event => {
            event.preventDefault();
            event.stopPropagation();
            onDragOverFolder(node.path);
          }}
          onDragOver={event => {
            event.preventDefault();
            event.stopPropagation();
            onDragOverFolder(node.path);
          }}
          onDragLeave={event => {
            if (isLeavingElement(event)) {
              onDragOverFolder(undefined);
            }
          }}
          onDrop={event => onDropOnFolder(event, node.path)}
          onDoubleClick={() => onToggleFolder(node.path)}
          style={treeRowStyle(colors, depth, isDropTarget)}
        >
          <TreeGuides colors={colors} depth={depth} />
          <button
            type="button"
            onClick={event => {
              event.stopPropagation();
              onToggleFolder(node.path);
            }}
            style={folderChevronStyle(colors)}
            aria-label={expanded ? 'Collapse folder' : 'Expand folder'}
          >
            <span style={chevronShapeStyle(colors, expanded)} />
          </button>
          <span style={treeTextStyle}>{node.name}</span>
        </div>
        {expanded &&
          node.children.map(child => (
            <TreeNodeView
              key={child.path}
              colors={colors}
              activeDocumentPath={activeDocumentPath}
              expandedFolders={expandedFolders}
              dragOverFolder={dragOverFolder}
              depth={depth + 1}
              forceExpanded={forceExpanded}
              node={child}
              onDeleteFile={onDeleteFile}
              onDeleteFolder={onDeleteFolder}
              onDragOverFolder={onDragOverFolder}
              onDropOnFolder={onDropOnFolder}
              onRenameFile={onRenameFile}
              onRenameFolder={onRenameFolder}
              onSelectDocument={onSelectDocument}
              onSetFocusedNode={onSetFocusedNode}
              onShowContextMenu={onShowContextMenu}
              onToggleFolder={onToggleFolder}
            />
          ))}
      </div>
    );
  }

  const selected = node.path === activeDocumentPath;
  return (
    <div
      draggable
      onContextMenu={event =>
        onShowContextMenu(event, {
          kind: 'file',
          path: node.path,
          document: node.document,
        })
      }
      onDragStart={event => {
        event.stopPropagation();
        event.dataTransfer.setData('text/x-knowledge-kind', 'file');
        event.dataTransfer.setData('text/x-knowledge-path', node.path);
      }}
      onDragEnd={() => onDragOverFolder(undefined)}
      style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
    >
      <ButtonBase
        onClick={() => onSelectDocument(node.path)}
        onMouseDown={() => onSetFocusedNode({ kind: 'file', path: node.path })}
        onDoubleClick={() => onRenameFile(node.document)}
        style={{
          ...treeRowStyle(colors, depth, selected),
          width: '100%',
          justifyContent: 'flex-start',
          color: selected ? colors.text.onAccent : colors.text.secondary,
        }}
      >
        <TreeGuides colors={colors} depth={depth} />
        <span style={folderChevronStyle(colors)}> </span>
        <span style={treeTextStyle}>{node.name}</span>
      </ButtonBase>
    </div>
  );
};

function ContextMenu(options: {
  colors: KnowledgeBuilderColors;
  contextMenu: ContextMenuState;
  onClose(): void;
  onCreateFile(path?: string): void;
  onCreateFolder(path?: string): void;
  onCopyFile(document: KnowledgeDocument): void;
  onCopyFolder(path: string): void;
  onPaste(path?: string): void;
  onRenameFile(document: KnowledgeDocument): void;
  onRenameFolder(path: string): void;
  onRemoveFile(document: KnowledgeDocument): void;
  onRemoveFolder(path: string): void;
  onSelectDocument(path: string): void;
  onUpload(): void;
}) {
  const { colors, contextMenu } = options;
  const run = (action: () => void) => {
    action();
    options.onClose();
  };
  const folderPath = contextMenu.kind === 'root' ? '' : contextMenu.path;
  return (
    <div
      style={{
        position: 'fixed',
        left: contextMenu.x,
        top: contextMenu.y,
        zIndex: 2000,
        minWidth: 160,
        padding: 4,
        background: colors.bg.panel,
        border: `1px solid ${colors.border.strong}`,
        borderRadius: 14,
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.35)',
        color: colors.text.secondary,
        fontSize: 12,
      }}
      onClick={event => event.stopPropagation()}
    >
      {contextMenu.kind === 'file' && (
        <>
          <ContextMenuItem
            colors={colors}
            onClick={() =>
              run(() => options.onSelectDocument(contextMenu.path))
            }
          >
            Open
          </ContextMenuItem>
          <ContextMenuItem
            colors={colors}
            onClick={() => run(() => options.onCopyFile(contextMenu.document))}
          >
            Copy
          </ContextMenuItem>
          <ContextMenuItem
            colors={colors}
            onClick={() =>
              run(() =>
                options.onPaste(
                  contextMenu.path.split('/').slice(0, -1).join('/'),
                ),
              )
            }
          >
            Paste
          </ContextMenuItem>
          <ContextMenuItem
            colors={colors}
            onClick={() =>
              run(() => options.onRenameFile(contextMenu.document))
            }
          >
            Rename
          </ContextMenuItem>
          <ContextMenuItem
            colors={colors}
            onClick={() =>
              run(() => options.onRemoveFile(contextMenu.document))
            }
          >
            Delete
          </ContextMenuItem>
        </>
      )}
      {contextMenu.kind !== 'file' && (
        <>
          <ContextMenuItem
            colors={colors}
            onClick={() => run(() => options.onCreateFile(folderPath))}
          >
            New File
          </ContextMenuItem>
          <ContextMenuItem
            colors={colors}
            onClick={() => run(() => options.onCreateFolder(folderPath))}
          >
            New Folder
          </ContextMenuItem>
          <ContextMenuItem
            colors={colors}
            onClick={() => run(() => options.onPaste(folderPath))}
          >
            Paste
          </ContextMenuItem>
          <ContextMenuItem
            colors={colors}
            onClick={() => run(options.onUpload)}
          >
            Upload Files
          </ContextMenuItem>
        </>
      )}
      {contextMenu.kind === 'folder' && (
        <>
          <ContextMenuSeparator colors={colors} />
          <ContextMenuItem
            colors={colors}
            onClick={() => run(() => options.onRenameFolder(contextMenu.path))}
          >
            Rename
          </ContextMenuItem>
          <ContextMenuItem
            colors={colors}
            onClick={() => run(() => options.onCopyFolder(contextMenu.path))}
          >
            Copy
          </ContextMenuItem>
          <ContextMenuItem
            colors={colors}
            onClick={() => run(() => options.onRemoveFolder(contextMenu.path))}
          >
            Delete
          </ContextMenuItem>
        </>
      )}
    </div>
  );
}

function ContextMenuItem(options: {
  colors: KnowledgeBuilderColors;
  children: string;
  onClick(): void;
}) {
  return (
    <button
      type="button"
      onClick={options.onClick}
      style={contextMenuItemStyle(options.colors)}
    >
      {options.children}
    </button>
  );
}

const ContextMenuSeparator = ({
  colors,
}: {
  colors: KnowledgeBuilderColors;
}) => <div style={contextMenuSeparatorStyle(colors)} />;

const TreeGuides = ({
  colors,
  depth,
}: {
  colors: KnowledgeBuilderColors;
  depth: number;
}) => {
  if (depth === 0) {
    return null;
  }
  return (
    <>
      {Array.from({ length: depth }).map((_, index) => (
        <span
          key={index}
          style={{
            position: 'absolute',
            left: 15 + index * 14,
            top: 0,
            bottom: 0,
            width: 1,
            background: colors.border.subtle,
            pointerEvents: 'none',
          }}
        />
      ))}
    </>
  );
};

function buildStyles(colors: KnowledgeBuilderColors) {
  return {
    treeShell: {
      flex: 1,
      minWidth: 0,
      border: `1px solid ${colors.border.default}`,
      borderRadius: 8,
      display: 'flex',
      flexDirection: 'column' as const,
      minHeight: 0,
      background: colors.bg.root,
      color: colors.text.secondary,
      overflow: 'hidden',
      fontSize: 12,
      fontFamily:
        '"Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif',
    },
    treeHeader: {
      minHeight: 26,
      display: 'flex',
      alignItems: 'center',
      padding: '0 10px',
      borderBottom: 0,
      background: colors.bg.panel,
    },
    rootHeader: {
      minHeight: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 6,
      padding: '0 6px',
      borderBottom: `1px solid ${colors.bg.panel}`,
      background: colors.bg.root,
    },
    rootHeaderRow: {
      display: 'flex',
      alignItems: 'center',
      minWidth: 0,
      flex: 1,
      color: colors.text.secondary,
    },
    treeHeaderText: {
      color: colors.text.secondary,
      fontSize: 11,
      letterSpacing: 0,
      textTransform: 'uppercase' as const,
    },
    rootText: {
      color: colors.text.primary,
      fontSize: 12,
      letterSpacing: 0,
      fontWeight: 600,
    },
    rootActions: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      flexShrink: 0,
    },
    rootActionButton: {
      width: 23,
      height: 23,
      padding: 3,
      borderRadius: 7,
      color: colors.text.secondary,
    },
    sourceBar: {
      display: 'flex',
      gap: 4,
      padding: 6,
      borderBottom: `1px solid ${colors.bg.panel}`,
    },
    sourceBarInput: {
      flex: 1,
      minWidth: 0,
      height: 26,
      border: `1px solid ${colors.border.strong}`,
      outline: 0,
      background: colors.bg.root,
      color: colors.text.primary,
      fontSize: 12,
      borderRadius: 13,
      padding: '0 12px',
      boxSizing: 'border-box' as const,
    },
    smallIconButton: {
      width: 28,
      height: 26,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${colors.border.strong}`,
      background: colors.bg.panelAlt,
      color: colors.text.secondary,
      cursor: 'pointer',
      borderRadius: 13,
    },
    treeBody: {
      flex: 1,
      minHeight: 0,
      overflow: 'auto',
      padding: '4px 0',
    },
    emptyTree: {
      padding: '8px 10px',
      color: colors.text.muted,
      fontSize: 12,
    },
  };
}

function treeRowStyle(
  colors: KnowledgeBuilderColors,
  depth: number,
  active: boolean,
) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    height: 22,
    padding: '0 8px',
    paddingLeft: 6 + depth * 14,
    position: 'relative' as const,
    background: active ? colors.bg.hover : 'transparent',
    borderRadius: 6,
    color: active ? colors.text.onAccent : colors.text.secondary,
    cursor: 'default',
    fontSize: 12,
    border: 0,
    textAlign: 'left' as const,
    justifyContent: 'flex-start',
  };
}

function folderChevronStyle(colors: KnowledgeBuilderColors) {
  return {
    width: 18,
    height: 20,
    flex: '0 0 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 0,
    padding: 0,
    background: 'transparent',
    color: colors.text.secondary,
    cursor: 'default',
  };
}

function chevronShapeStyle(colors: KnowledgeBuilderColors, expanded: boolean) {
  return {
    width: 8,
    height: 8,
    borderRight: `1.5px solid ${colors.text.secondary}`,
    borderBottom: `1.5px solid ${colors.text.secondary}`,
    transform: expanded ? 'rotate(45deg)' : 'rotate(-45deg)',
    transformOrigin: 'center',
  };
}

const treeTextStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
  minWidth: 0,
};

function contextMenuItemStyle(colors: KnowledgeBuilderColors) {
  return {
    display: 'block',
    width: '100%',
    padding: '6px 24px 6px 10px',
    border: 0,
    background: 'transparent',
    color: colors.text.secondary,
    textAlign: 'left' as const,
    fontSize: 12,
    cursor: 'pointer',
    borderRadius: 9,
  };
}

function contextMenuSeparatorStyle(colors: KnowledgeBuilderColors) {
  return {
    height: 1,
    margin: '4px 0',
    background: colors.border.strong,
  };
}

function buildTree(documents: KnowledgeDocument[], directories: string[]) {
  const root: TreeNode[] = [];

  for (const directory of directories) {
    ensureFolder(root, directory);
  }

  for (const document of documents) {
    const parts = document.path.split('/');
    let siblings = root;
    let currentPath = '';
    for (let index = 0; index < parts.length; index += 1) {
      const part = parts[index];
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      if (index === parts.length - 1) {
        siblings.push({
          kind: 'file',
          name: part,
          path: document.path,
          document,
        });
        continue;
      }
      siblings = ensureChildFolder(siblings, part, currentPath).children;
    }
  }

  return sortTree(root);
}

function isHiddenDocumentPath(path: string, patterns: string[]) {
  const normalizedPath = normalizePath(path);
  return patterns.some(pattern => {
    const normalizedPattern = normalizePath(pattern);
    if (!normalizedPattern) {
      return false;
    }
    if (normalizedPattern.endsWith('/**')) {
      const directory = normalizedPattern.slice(0, -3).replace(/\/$/, '');
      return (
        normalizedPath === directory ||
        normalizedPath.startsWith(`${directory}/`)
      );
    }
    return normalizedPath === normalizedPattern;
  });
}

function ensureFolder(nodes: TreeNode[], path: string) {
  const parts = path.split('/').filter(Boolean);
  let siblings = nodes;
  let folder: Extract<TreeNode, { kind: 'folder' }> | undefined;
  let currentPath = '';
  for (const part of parts) {
    currentPath = currentPath ? `${currentPath}/${part}` : part;
    folder = siblings.find(
      item => item.kind === 'folder' && item.path === currentPath,
    ) as Extract<TreeNode, { kind: 'folder' }> | undefined;
    if (!folder) {
      folder = {
        kind: 'folder',
        name: part,
        path: currentPath,
        children: [],
      };
      siblings.push(folder);
    }
    siblings = folder.children;
  }
  return folder as Extract<TreeNode, { kind: 'folder' }>;
}

function ensureChildFolder(nodes: TreeNode[], name: string, path: string) {
  let folder = nodes.find(
    item => item.kind === 'folder' && item.path === path,
  ) as Extract<TreeNode, { kind: 'folder' }> | undefined;
  if (!folder) {
    folder = {
      kind: 'folder',
      name,
      path,
      children: [],
    };
    nodes.push(folder);
  }
  return folder;
}

function sortTree(nodes: TreeNode[]): TreeNode[] {
  return nodes
    .map(node =>
      node.kind === 'folder'
        ? { ...node, children: sortTree(node.children) }
        : node,
    )
    .sort((a, b) => {
      if (a.kind !== b.kind) {
        return a.kind === 'folder' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
}

function normalizePath(path: string) {
  return path.trim().replace(/^\/+/, '').replace(/\/+/g, '/');
}

function joinPath(folder: string, name: string) {
  return [folder, name].filter(Boolean).join('/');
}

function nextAvailableFilePath(path: string, documents: KnowledgeDocument[]) {
  const existingPaths = new Set(documents.map(document => document.path));
  if (!existingPaths.has(path)) {
    return path;
  }
  const extensionMatch = path.match(/(\.[^./]+)$/);
  const extension = extensionMatch?.[1] ?? '';
  const stem = extension ? path.slice(0, -extension.length) : path;
  for (let index = 2; index < 1000; index += 1) {
    const candidate = `${stem}-${index}${extension}`;
    if (!existingPaths.has(candidate)) {
      return candidate;
    }
  }
  return path;
}

function nextAvailableFolderPath(path: string, documents: KnowledgeDocument[]) {
  const existingPaths = new Set(documents.map(document => document.path));
  if (!existingPaths.has(joinPath(path, 'index.md'))) {
    return path;
  }
  for (let index = 2; index < 1000; index += 1) {
    const candidate = `${path}-${index}`;
    if (!existingPaths.has(joinPath(candidate, 'index.md'))) {
      return candidate;
    }
  }
  return path;
}

function isLeavingElement(event: DragEvent<HTMLElement>) {
  const nextTarget = event.relatedTarget;
  return (
    !nextTarget ||
    !(nextTarget instanceof Node) ||
    !event.currentTarget.contains(nextTarget)
  );
}
